import { PLUGIN_REGISTRY } from '@/plugins/registry'

import React, { createContext, useContext, useEffect } from 'react'

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'

import { BusinessModel } from '@/http/models/business/Business'

import { PluginDefinition, PluginState } from '@/types/plugin'

interface PluginContextType {
  activePlugins: string[]
  installPlugin: (pluginId: string) => Promise<boolean>
  uninstallPlugin: (pluginId: string) => Promise<boolean>
  savePluginConfig: (pluginId: string, config: Record<string, unknown>) => Promise<boolean>
  isPluginInstalled: (pluginId: string) => boolean
  getPluginRoutes: () => PluginDefinition['routes']
  getPluginNavItems: () => PluginDefinition['navItems']
  getPluginConfig: <T = Record<string, unknown>>(pluginId: string) => T | undefined
  availablePlugins: PluginDefinition[]
}

const PluginContext = createContext<PluginContextType | undefined>(undefined)

/**
 * Installed plugins and their settings, kept per store in `app_settings.plugins` on the business profile (saved like
 * the data layer settings), so every staff member and device sees the same plugins.
 */
export const PluginProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { businessProfile, refreshFeatureFlags } = useFeatureFlags()
  const plugins: Record<string, PluginState> = businessProfile?.app_settings?.plugins ?? {}
  const activePlugins = Object.keys(plugins).filter((id) => plugins[id]?.active && PLUGIN_REGISTRY.some((p) => p.id === id))

  // Plugins used to be kept in the browser, the removed Vault plugin's S3 keys among them: clear what is left.
  useEffect(() => {
    try {
      localStorage.removeItem('activePlugins')
      localStorage.removeItem('pluginConfigs')
    } catch {
      // Storage blocked: nothing was saved there either.
    }
  }, [])

  const savePlugins = async (next: Record<string, PluginState>) => {
    try {
      const response = await BusinessModel.updateBusinessProfile({
        body: { app_settings: { ...businessProfile?.app_settings, plugins: next } },
      })
      if (!response.success) return false
      await refreshFeatureFlags()
      return true
    } catch (error) {
      console.error('Could not save plugin settings', error)
      return false
    }
  }

  const installPlugin = (pluginId: string) => savePlugins({ ...plugins, [pluginId]: { ...plugins[pluginId], active: true } })

  const uninstallPlugin = (pluginId: string) => {
    const next = { ...plugins }
    delete next[pluginId]
    return savePlugins(next)
  }

  const savePluginConfig = (pluginId: string, config: Record<string, unknown>) =>
    savePlugins({ ...plugins, [pluginId]: { active: plugins[pluginId]?.active ?? false, config } })

  const getPluginConfig = <T,>(pluginId: string) => plugins[pluginId]?.config as T | undefined

  const isPluginInstalled = (pluginId: string) => activePlugins.includes(pluginId)

  const getPluginRoutes = () => activePlugins.flatMap((id) => PLUGIN_REGISTRY.find((p) => p.id === id)?.routes ?? [])

  const getPluginNavItems = () =>
    activePlugins.flatMap((id) => {
      const plugin = PLUGIN_REGISTRY.find((p) => p.id === id)
      return plugin ? plugin.navigation || plugin.navItems || [] : []
    })

  return (
    <PluginContext.Provider
      value={{
        activePlugins,
        installPlugin,
        uninstallPlugin,
        savePluginConfig,
        isPluginInstalled,
        getPluginRoutes,
        getPluginNavItems,
        getPluginConfig,
        availablePlugins: PLUGIN_REGISTRY,
      }}
    >
      {children}
    </PluginContext.Provider>
  )
}

export const usePlugins = () => {
  const context = useContext(PluginContext)
  if (context === undefined) {
    throw new Error('usePlugins must be used within a PluginProvider')
  }
  return context
}
